import { MessageCircle, Mail, MapPin, Clock, ShieldCheck, Copy, Award } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LANDLORD } from "@/data/landlord";

export default function LandlordContact() {
  const [licences, setLicences] = useState(LANDLORD.licences);

  useEffect(() => {
    api
      .get("/site-settings")
      .then((r) => {
        if (r.data?.licences?.length) setLicences(r.data.licences);
      })
      .catch(() => {});
  }, []);

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <section id="contact" className="py-16 lg:py-24">
      <div
        data-testid="landlord-contact-card"
        className="rounded-3xl border border-[#E4DBD0] dark:border-[#352F2B] bg-[#F4EFEA] dark:bg-[#231F1C] p-8 sm:p-12 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10"
      >
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#A05A32] dark:text-[#D9773F] mb-4">
            A Professional Lettings Service
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-[#1A1715] dark:text-[#F7F4F0]">
            Speak directly with our <span className="italic">experienced lettings team.</span>
          </h2>
          <p className="mt-4 text-base text-[#57524E] dark:text-[#C7BEB5] leading-relaxed">
            {LANDLORD.statement}
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-[#2D4B3E] dark:text-[#4E826B]">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            Professional service. Clear communication. Quality homes.
          </div>
          <p className="mt-8 font-serif text-xl text-[#1A1715] dark:text-[#F7F4F0]">{LANDLORD.name}</p>
          <p className="text-sm text-[#8A837C] dark:text-[#8E867E]">{LANDLORD.title}</p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            data-testid="landlord-phone-link"
            href={LANDLORD.phoneHref}
            target="_blank"
            rel="noopener noreferrer"
            className="group rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-6 hover:border-[#A05A32] transition-colors"
          >
            <MessageCircle className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
            <p className="mt-3 text-xs uppercase tracking-[0.2em] font-mono text-[#8A837C] dark:text-[#8E867E]">WhatsApp Us</p>
            <p className="mt-1 text-lg font-medium text-[#1A1715] dark:text-[#F7F4F0]">{LANDLORD.phone}</p>
            <button
              data-testid="landlord-phone-copy-btn"
              onClick={(e) => { e.preventDefault(); copy(LANDLORD.phone, "Phone number"); }}
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#A05A32] dark:text-[#D9773F] hover:underline"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </a>
          <a
            data-testid="landlord-email-link"
            href={LANDLORD.emailHref}
            className="group rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-6 hover:border-[#A05A32] transition-colors"
          >
            <Mail className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
            <p className="mt-3 text-xs uppercase tracking-[0.2em] font-mono text-[#8A837C] dark:text-[#8E867E]">Email Us</p>
            <p className="mt-1 text-lg font-medium break-all text-[#1A1715] dark:text-[#F7F4F0]">{LANDLORD.email}</p>
            <button
              data-testid="landlord-email-copy-btn"
              onClick={(e) => { e.preventDefault(); copy(LANDLORD.email, "Email address"); }}
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#A05A32] dark:text-[#D9773F] hover:underline"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </a>
          <div className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-6">
            <Clock className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
            <p className="mt-3 text-xs uppercase tracking-[0.2em] font-mono text-[#8A837C] dark:text-[#8E867E]">Response Time</p>
            <p className="mt-1 text-sm text-[#57524E] dark:text-[#C7BEB5] leading-relaxed">{LANDLORD.hours}</p>
          </div>
          <div className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-6">
            <MapPin className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
            <p className="mt-3 text-xs uppercase tracking-[0.2em] font-mono text-[#8A837C] dark:text-[#8E867E]">Office</p>
            <p className="mt-1 text-sm text-[#57524E] dark:text-[#C7BEB5] leading-relaxed">{LANDLORD.office}</p>
          </div>
        </div>
        <div className="lg:col-span-12">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#8A837C] dark:text-[#8E867E] mb-4">
            Licences & Accreditations
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {licences.map((l, i) => (
              <div
                key={l.id ?? i}
                data-testid={`licence-slot-${l.id ?? i + 1}`}
                className="rounded-2xl border border-dashed border-[#E4DBD0] dark:border-[#352F2B] bg-white/60 dark:bg-[#1A1715] p-5 text-center"
              >
                {l.logo ? (
                  <img src={l.logo} alt={l.name || `Licence ${i + 1}`} className="mx-auto h-12 w-auto object-contain" />
                ) : (
                  <div className="mx-auto h-12 w-12 rounded-lg bg-[#F4EFEA] dark:bg-[#231F1C] flex items-center justify-center">
                    <Award className="h-5 w-5 text-[#8A837C] dark:text-[#8E867E]" />
                  </div>
                )}
                <p className="mt-3 text-sm font-medium text-[#1A1715] dark:text-[#F7F4F0]">{l.name || `Licence ${i + 1}`}</p>
                <p className="mt-1 text-xs text-[#8A837C] dark:text-[#8E867E]">{l.text || "Licence details"}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
